'use client';

import { useTranslations } from 'next-intl';
import type { BreakEven } from '@/lib/audit/break-even';
import { formatCents } from '@/lib/audit/break-even';
import styles from './BreakEvenBlock.module.css';

/**
 * The money layer of a paid report (B-05). States a break-even from the
 * client's own number, never a forecast. The no-money variant covers pages
 * whose success is finding information or completing a task. Copy lives in
 * the `proReport.breakEven` namespace of the message files.
 */

interface Props {
  breakEven: BreakEven;
  locale?: string;
}

const PATH_KEY = { purchase: 'pathPurchase', contact: 'pathContact', signup: 'pathSignup' } as const;
const NOUN_KEY = { purchase: 'nounPurchase', contact: 'nounContact', signup: 'nounSignup' } as const;

export function BreakEvenBlock({ breakEven, locale = 'en' }: Props) {
  const t = useTranslations('proReport.breakEven');
  const lang: 'en' | 'es' = locale === 'es' ? 'es' : 'en';
  const nf = new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US');

  if (breakEven.kind === 'missing') return null;

  if (breakEven.kind === 'task') {
    return (
      <section className={styles.block} aria-labelledby="break-even-heading">
        <span className={styles.label}>{t('label')}</span>
        <h2 id="break-even-heading" className={styles.headline}>
          {t('taskHeadline', { count: breakEven.onPathCount, total: breakEven.totalFindings })}
        </h2>
        <p className={styles.note}>{t('taskNote')}</p>
      </section>
    );
  }

  const noun = t(NOUN_KEY[breakEven.successAction]);
  const path = t(PATH_KEY[breakEven.successAction]);
  const value = formatCents(breakEven.conversionValueCents, lang);

  return (
    <section className={styles.block} aria-labelledby="break-even-heading">
      <span className={styles.label}>{t('label')}</span>
      <h2 id="break-even-heading" className={styles.headline}>
        {t('onPath', { count: breakEven.onPathCount, total: breakEven.totalFindings, path })}
        {breakEven.onPathQuickCount > 0 ? ` ${t('quick', { count: breakEven.onPathQuickCount })}` : ''}
      </h2>
      <p className={styles.told}>{t('told', { noun, value })}</p>
      <p className={styles.breakEven}>{t('breakEven', { noun })}</p>

      {breakEven.scenarios && (
        <div className={styles.scenarios}>
          <span className={styles.label}>{t('scenariosLabel')}</span>
          <p className={styles.note}>
            {t('notCompleting', { count: nf.format(breakEven.scenarios.notCompletingPerMonth) })}
          </p>
          <ul className={styles.scenarioList}>
            {breakEven.scenarios.list.map((s) => {
              const rate = `${Math.round(s.recovery * 100)}%`;
              return (
                <li key={s.recovery} className={styles.scenario}>
                  <span className={styles.scenarioRate}>{rate}</span>
                  <span>{t('recover', { rate, value: formatCents(s.monthlyCents, lang) })}</span>
                </li>
              );
            })}
          </ul>
          <p className={styles.assumption}>{t('assumption')}</p>
        </div>
      )}
    </section>
  );
}
